/**
 * Private-Score: Bedarfsgemeinschaft und privates Umfeld.
 *
 * Heuristik (Konzept §4.2 Dimension 6) — kombiniert qualitative Daten
 * (Kinderbetreuung, Pflege, Zumutbarkeitsgründe) mit der quantitativen
 * Tragfähigkeitslücke aus dem BG-Bedarfsrechner (gap = bedarf - einkommen
 * in Cent).
 *
 *   - Basis 30
 *   - Kinderbetreuung gesichert ODER nicht zutreffend: +15
 *   - keine Pflegeverantwortung ODER beschrieben: +10
 *   - Zumutbarkeitsgründe formuliert (≥ 50 Zeichen): +10
 *   - regionaler Arbeitsmarkt-Kontext beschrieben (≥ 30 Zeichen): +5
 *   - BG-Lücke (falls bekannt) in Cents:
 *       * ≤ 0       (keine Lücke):       +30
 *       * ≤ 30_000  (≤ 300€):            +20
 *       * ≤ 80_000  (≤ 800€):            +10
 *       * > 80_000  (große Lücke):       +0
 *     Falls Lücke unbekannt: +10 (neutral, „BG noch nicht erfasst —
 *     der Score-Beitrag ist eine Annahme; sobald die Dimension komplett
 *     erfasst wird, ersetzt der echte Wert diesen Default").
 *
 * `bgGapCents` ist optional — wird vom BG-Modul (`computeTragfaehigkeitsLuecke`)
 * geliefert. Damit ist Score-Engine + BG-Engine entkoppelt: die Score-Engine
 * konsumiert das Ergebnis, berechnet es nicht selbst.
 *
 * Hinweis: Wenn die Private-Dimension noch gar nicht erfasst wurde, wird sie
 * von `computeScoreResult` bereits aus dem Total-Score ausgeschlossen — diese
 * Funktion sieht in dem Fall niemals einen Default. Der „neutral +10"-Pfad
 * unten greift also nur, wenn die Dimension touched ist, aber die BG-Daten
 * (in Resources/4) noch fehlen.
 */
import type { ScoreValue } from '../schema/brands.js';
import type { PrivateData } from '../schema/dimensionData.js';
import { clampScore } from './internal/clamp.js';

export function computePrivateScore(data: PrivateData, bgGapCents?: number): ScoreValue {
  let score = 30;

  // 'secured' und 'not_applicable' beide → +15.
  // 'not_secured' wird über getRiskIndicators als Risiko ausgewiesen.
  if (data.childcareStatus === 'secured' || data.childcareStatus === 'not_applicable') {
    score += 15;
  }

  if (
    !data.hasCareResponsibility ||
    (data.careResponsibilityDescription ?? '').trim().length >= 30
  ) {
    score += 10;
  }

  if ((data.zumutbarkeitsgruende ?? '').trim().length >= 50) score += 10;

  if ((data.regionalLaborMarketContext ?? '').trim().length >= 30) score += 5;

  if (typeof bgGapCents === 'number') {
    if (bgGapCents <= 0) score += 30;
    else if (bgGapCents <= 30_000) score += 20;
    else if (bgGapCents <= 80_000) score += 10;
    // > 80_000 → 0
  } else {
    score += 10;
  }

  return clampScore(score);
}
